import React from 'react';

interface StoplightSummaryProps {
  summary: {
    active: number;
    warning: number;
    inactive: number;
    total: number;
  };
  onFilterClick?: (filter: 'active' | 'warning' | 'inactive') => void;
  loading?: boolean;
}

const StoplightSummary: React.FC<StoplightSummaryProps> = ({ summary, onFilterClick, loading = false }) => {
  const activePercentage = summary.total > 0 ? Math.round((summary.active / summary.total) * 100) : 0;
  const warningPercentage = summary.total > 0 ? Math.round((summary.warning / summary.total) * 100) : 0;
  const inactivePercentage = summary.total > 0 ? Math.round((summary.inactive / summary.total) * 100) : 0;

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 relative">
      {/* Loading Overlay */}
      {loading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 rounded-lg flex items-center justify-center z-10">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="text-gray-600 font-medium">Updating summary...</p>
          </div>
        </div>
      )}

      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Platform Engagement Overview</h2>
        <p className="text-gray-600">Login activity status for ACTIVE customers - identifying who's using the platform</p>
      </div>

      {/* Main Stoplight Visualization */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Active - Green */}
        <div
          className="relative bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6 border-2 border-green-200 hover:shadow-lg transition-shadow cursor-pointer hover:scale-105 transition-transform"
          onClick={() => onFilterClick?.('active')}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-white text-2xl">✓</span>
              </div>
              <div>
                <h3 className="text-lg font-bold text-green-900">Using Platform</h3>
                <p className="text-xs text-green-700">Logged in ≤ 7 days</p>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold text-green-900">{summary.active}</span>
              <span className="text-lg text-green-700">customers</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-green-200 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-green-600 h-full rounded-full transition-all duration-500"
                  style={{ width: `${activePercentage}%` }}
                ></div>
              </div>
              <span className="text-sm font-semibold text-green-900">{activePercentage}%</span>
            </div>
          </div>
        </div>

        {/* Warning - Yellow */}
        <div
          className="relative bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-6 border-2 border-yellow-300 hover:shadow-lg transition-shadow cursor-pointer hover:scale-105 transition-transform"
          onClick={() => onFilterClick?.('warning')}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-white text-2xl">⚠</span>
              </div>
              <div>
                <h3 className="text-lg font-bold text-yellow-900">At Risk</h3>
                <p className="text-xs text-yellow-700">Logged in 8-30 days</p>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold text-yellow-900">{summary.warning}</span>
              <span className="text-lg text-yellow-700">customers</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-yellow-200 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-yellow-600 h-full rounded-full transition-all duration-500"
                  style={{ width: `${warningPercentage}%` }}
                ></div>
              </div>
              <span className="text-sm font-semibold text-yellow-900">{warningPercentage}%</span>
            </div>
          </div>
        </div>

        {/* Inactive - Red */}
        <div
          className="relative bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-6 border-2 border-red-300 hover:shadow-lg transition-shadow ring-2 ring-red-400 ring-opacity-50 cursor-pointer hover:scale-105 transition-transform"
          onClick={() => onFilterClick?.('inactive')}
        >
          <div className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
            NEEDS ATTENTION
          </div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                <span className="text-white text-2xl">✕</span>
              </div>
              <div>
                <h3 className="text-lg font-bold text-red-900">Not Using Platform</h3>
                <p className="text-xs text-red-700">No login &gt; 30 days</p>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold text-red-900">{summary.inactive}</span>
              <span className="text-lg text-red-700">customers</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-red-200 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-red-600 h-full rounded-full transition-all duration-500"
                  style={{ width: `${inactivePercentage}%` }}
                ></div>
              </div>
              <span className="text-sm font-semibold text-red-900">{inactivePercentage}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Combined Progress Bar */}
      <div className="bg-gray-50 rounded-lg p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-800">Overall Distribution</h3>
          <span className="text-sm text-gray-600">Total: {summary.total} customers</span>
        </div>
        <div className="relative w-full h-8 bg-gray-200 rounded-full overflow-hidden flex">
          {activePercentage > 0 && (
            <div
              className="bg-green-500 h-full flex items-center justify-center text-white text-xs font-medium transition-all duration-500"
              style={{ width: `${activePercentage}%` }}
            >
              {activePercentage > 10 && `${activePercentage}%`}
            </div>
          )}
          {warningPercentage > 0 && (
            <div
              className="bg-yellow-500 h-full flex items-center justify-center text-white text-xs font-medium transition-all duration-500"
              style={{ width: `${warningPercentage}%` }}
            >
              {warningPercentage > 10 && `${warningPercentage}%`}
            </div>
          )}
          {inactivePercentage > 0 && (
            <div
              className="bg-red-500 h-full flex items-center justify-center text-white text-xs font-medium transition-all duration-500"
              style={{ width: `${inactivePercentage}%` }}
            >
              {inactivePercentage > 10 && `${inactivePercentage}%`}
            </div>
          )}
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-600">
          <span>More Engaged</span>
          <span>Less Engaged</span>
        </div>
      </div>

      {/* Alert for High Inactive Rate */}
      {inactivePercentage > 30 && (
        <div className="mt-6 bg-red-50 border-l-4 border-red-500 p-4 rounded">
          <div className="flex items-start gap-3">
            <span className="text-red-500 text-xl">🚨</span>
            <div>
              <h4 className="text-red-800 font-semibold">High Non-Usage Rate Detected</h4>
              <p className="text-red-700 text-sm mt-1">
                Over {inactivePercentage}% of your ACTIVE customers haven't logged into the platform recently. Consider reaching out to re-engage these users.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Success Message for High Active Rate */}
      {activePercentage > 70 && (
        <div className="mt-6 bg-green-50 border-l-4 border-green-500 p-4 rounded">
          <div className="flex items-start gap-3">
            <span className="text-green-500 text-xl">🎉</span>
            <div>
              <h4 className="text-green-800 font-semibold">Excellent Platform Engagement!</h4>
              <p className="text-green-700 text-sm mt-1">
                {activePercentage}% of your ACTIVE customers are regularly using the platform. Keep up the great work!
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoplightSummary;
